import {
    ActionIcon,
    Alert,
    Anchor,
    Button,
    Checkbox,
    Group,
    Menu,
    Modal,
    Paper,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import {IconAlertCircle, IconEdit, IconPlus, IconTrash} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'
import {useMemo, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'

import {InvoiceStatusBadge} from '../invoices/invoice-status-badge'
import {useCreateInvoice, useInvoices} from '../invoices/use-invoices'
import {getActiveProjectBillingTerms} from '../jobs/billing-terms-display'
import {useEditRights} from '../jobs/edit-rights-context'
import {formatPrice} from '../lib/format-price'
import {ProjectFinancialLineItemModal} from './project-financial-line-item-modal'
import {groupUnbilledCharges} from './project-invoicing-utils'
import type {ProjectBillableLineItem, ProjectDetail} from './schemas'
import {
    useArchiveProjectBillableLineItem,
    useProjectBillableLineItems,
    useProjectBillingTerms,
} from './use-projects'

type ProjectInvoicingSectionProps = {
    project: ProjectDetail
}

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

export function ProjectInvoicingSection({project}: ProjectInvoicingSectionProps) {
    const {canEdit} = useEditRights()
    const navigate = useNavigate()
    const createInvoice = useCreateInvoice()
    const {data: allInvoices = []} = useInvoices()
    const {data: lineItems = []} = useProjectBillableLineItems(project.id)
    const {data: billingTerms = []} = useProjectBillingTerms(project.id)

    const [selectedLineItemIds, setSelectedLineItemIds] = useState<string[]>([])
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [lineItemModalOpened, setLineItemModalOpened] = useState(false)
    const [editingLineItem, setEditingLineItem] = useState<ProjectBillableLineItem | undefined>()
    const [presetDraft, setPresetDraft] = useState<
        Partial<Parameters<typeof ProjectFinancialLineItemModal>[0]['initialDraft']> | undefined
    >()
    const [pendingArchiveLineItem, setPendingArchiveLineItem] = useState<
        ProjectBillableLineItem | undefined
    >()

    const archiveMutation = useArchiveProjectBillableLineItem(project.id)
    const archiving = archiveMutation.isPending

    const activeTerms = getActiveProjectBillingTerms(billingTerms)
    const hseiPercent = activeTerms?.hseiPercent ?? null

    const projectInvoices = useMemo(
        () => allInvoices.filter((invoice) => invoice.projectId === project.id),
        [allInvoices, project.id],
    )

    const unbilledLineItems = useMemo(
        () => lineItems.filter((item) => item.invoiceId === null),
        [lineItems],
    )

    const groupedCharges = useMemo(
        () => groupUnbilledCharges(lineItems, project.jobs),
        [lineItems, project.jobs],
    )

    const selectedTotal = useMemo(
        () =>
            unbilledLineItems
                .filter((item) => selectedLineItemIds.includes(item.id))
                .reduce((sum, item) => sum + item.totalPrice, 0),
        [selectedLineItemIds, unbilledLineItems],
    )

    const selectedLineItems = useMemo(
        () => unbilledLineItems.filter((item) => selectedLineItemIds.includes(item.id)),
        [selectedLineItemIds, unbilledLineItems],
    )

    const selectedProvenance = selectedLineItems[0] ?? null
    const selectedGroupIsHomogeneous =
        selectedProvenance == null ||
        selectedLineItems.every(
            (item) =>
                item.poNumber === selectedProvenance.poNumber &&
                item.projectBillingTermsId === selectedProvenance.projectBillingTermsId,
        )

    const hseiSourceItems = selectedLineItems.length > 0 ? selectedLineItems : unbilledLineItems
    const hseiSource = hseiSourceItems[0] ?? null
    const hseiGroupIsHomogeneous =
        hseiSource != null &&
        hseiSourceItems.every(
            (item) =>
                item.poNumber === hseiSource.poNumber &&
                item.projectBillingTermsId === hseiSource.projectBillingTermsId,
        )

    const hseiSubtotal = useMemo(() => {
        if (selectedLineItemIds.length > 0) {
            return selectedTotal
        }
        return unbilledLineItems.reduce((sum, item) => sum + item.totalPrice, 0)
    }, [selectedLineItemIds, selectedTotal, unbilledLineItems])

    const hseiAmount = useMemo(() => {
        if (hseiPercent === null) {
            return 0
        }
        return hseiSubtotal * (hseiPercent / 100)
    }, [hseiPercent, hseiSubtotal])

    const isProjectArchived = project.archivedAt !== null

    function toggleLineItem(lineItemId: string, checked: boolean) {
        const lineItem = unbilledLineItems.find((item) => item.id === lineItemId)
        if (
            checked &&
            selectedProvenance != null &&
            lineItem != null &&
            (lineItem.poNumber !== selectedProvenance.poNumber ||
                lineItem.projectBillingTermsId !== selectedProvenance.projectBillingTermsId)
        ) {
            return
        }
        setSelectedLineItemIds((current) =>
            checked ? [...current, lineItemId] : current.filter((id) => id !== lineItemId),
        )
    }

    function openAddModal(draft?: typeof presetDraft) {
        setEditingLineItem(undefined)
        setPresetDraft(draft)
        setLineItemModalOpened(true)
    }

    function openEditModal(lineItem: ProjectBillableLineItem) {
        setEditingLineItem(lineItem)
        setPresetDraft(undefined)
        setLineItemModalOpened(true)
    }

    function closeLineItemModal() {
        setLineItemModalOpened(false)
        setEditingLineItem(undefined)
        setPresetDraft(undefined)
    }

    async function handleCreateInvoice() {
        if (isProjectArchived) {
            return
        }
        if (!selectedGroupIsHomogeneous) {
            setErrorMessage('Select charges from one PO and billing-terms group at a time.')
            return
        }
        setErrorMessage(null)

        try {
            const invoice = await createInvoice.mutateAsync({
                projectId: project.id,
                lineItemIds: selectedLineItemIds,
            })
            setSelectedLineItemIds([])
            await navigate({
                to: '/invoices/$invoiceId',
                params: {invoiceId: invoice.id},
            })
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to create invoice')
        }
    }

    async function handleArchiveLineItem() {
        if (!pendingArchiveLineItem) {
            return
        }

        setErrorMessage(null)
        try {
            await archiveMutation.mutateAsync(pendingArchiveLineItem.id)
            setSelectedLineItemIds((current) =>
                current.filter((id) => id !== pendingArchiveLineItem.id),
            )
            setPendingArchiveLineItem(undefined)
        } catch (error) {
            setErrorMessage(
                error instanceof Error ? error.message : 'Failed to remove financial line item',
            )
        }
    }

    function handleAddHsei() {
        if (hseiPercent === null || !hseiGroupIsHomogeneous || hseiSource == null) {
            setErrorMessage('HSEI requires one PO and billing-terms group.')
            return
        }
        openAddModal({
            jobId: null,
            chargeType: 'manual',
            name: 'HSEI Fee',
            description: `HSEI Fee (${hseiPercent}% of ${formatPrice(hseiSubtotal)})`,
            quantity: 1,
            unit: 'each',
            unitPrice: Number(hseiAmount.toFixed(2)),
            poNumber: hseiSource.poNumber,
            projectBillingTermsId: hseiSource.projectBillingTermsId,
        })
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={4}>
                        <Title order={3}>Invoicing</Title>
                        <Text size="sm" c="dimmed">
                            Add charges, select unbilled items to create a draft invoice, or open an
                            existing invoice for this order.
                        </Text>
                    </Stack>
                    {canEdit ? (
                        <Menu position="bottom-end" withinPortal={false}>
                            <Menu.Target>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    variant="light"
                                    size="sm"
                                >
                                    Add charge
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item onClick={() => openAddModal()}>Custom charge</Menu.Item>
                                {hseiPercent !== null && (
                                    <Menu.Item onClick={handleAddHsei}>
                                        Add HSEI Fee ({hseiPercent}%)
                                    </Menu.Item>
                                )}
                            </Menu.Dropdown>
                        </Menu>
                    ) : null}
                </Group>

                {isProjectArchived && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        title="Archived order"
                        color="yellow"
                    >
                        This order is archived. Archived orders cannot be invoiced.
                    </Alert>
                )}

                {projectInvoices.length > 0 && (
                    <Stack gap="sm">
                        <Text size="sm" fw={500}>
                            Invoices for this order
                        </Text>
                        <Stack gap="xs">
                            {projectInvoices.map((invoice) => (
                                <Group key={invoice.id} justify="space-between" wrap="nowrap">
                                    <Group gap="sm" wrap="nowrap">
                                        <Anchor
                                            component={Link}
                                            to="/invoices/$invoiceId"
                                            params={{invoiceId: invoice.id}}
                                            size="sm"
                                            fw={500}
                                        >
                                            Invoice
                                        </Anchor>
                                        <InvoiceStatusBadge
                                            isVoided={invoice.isVoided}
                                            isDraft={invoice.isDraft}
                                        />
                                        <Text size="sm" c="dimmed">
                                            {formatCreatedAt(invoice.createdAt)} ·{' '}
                                            {invoice.lineItemCount}{' '}
                                            {invoice.lineItemCount === 1 ? 'item' : 'items'}
                                        </Text>
                                    </Group>
                                    <Stack gap={0} align="flex-end">
                                        <Text size="sm" c="dimmed">
                                            PO: {invoice.poNumber ?? 'No PO'}
                                        </Text>
                                        <Text size="sm" fw={500}>
                                            {formatPrice(Number(invoice.total))}
                                        </Text>
                                    </Stack>
                                </Group>
                            ))}
                        </Stack>
                    </Stack>
                )}

                <Stack gap="sm">
                    <Text size="sm" fw={500}>
                        Unbilled charges
                    </Text>
                    {groupedCharges.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No unbilled charges on this order.
                        </Text>
                    ) : (
                        <Stack gap="md">
                            {groupedCharges.map((poGroup) => (
                                <Stack key={poGroup.poNumber ?? 'no-po'} gap="sm">
                                    <Text size="sm" fw={700}>
                                        PO: {poGroup.poNumber ?? 'No PO'}
                                    </Text>
                                    {poGroup.jobGroups.map((group) => (
                                        <Stack key={group.jobId ?? 'project-level'} gap="xs">
                                            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                                                {group.jobName}
                                            </Text>
                                            <Stack gap="xs">
                                                {group.items.map((lineItem) => (
                                                    <Group
                                                        key={lineItem.id}
                                                        justify="space-between"
                                                        wrap="nowrap"
                                                    >
                                                        <Checkbox
                                                            aria-label={lineItem.name}
                                                            checked={selectedLineItemIds.includes(
                                                                lineItem.id,
                                                            )}
                                                            disabled={
                                                                selectedProvenance != null &&
                                                                (lineItem.poNumber !==
                                                                    selectedProvenance.poNumber ||
                                                                    lineItem.projectBillingTermsId !==
                                                                        selectedProvenance.projectBillingTermsId)
                                                            }
                                                            onChange={(event) =>
                                                                toggleLineItem(
                                                                    lineItem.id,
                                                                    event.target.checked,
                                                                )
                                                            }
                                                            label={
                                                                <Group
                                                                    justify="space-between"
                                                                    wrap="nowrap"
                                                                    w="100%"
                                                                    align="flex-start"
                                                                >
                                                                    <Stack gap={2}>
                                                                        <Text size="sm">
                                                                            {lineItem.name}
                                                                        </Text>
                                                                        {lineItem.description && (
                                                                            <Text
                                                                                size="xs"
                                                                                c="dimmed"
                                                                            >
                                                                                {
                                                                                    lineItem.description
                                                                                }
                                                                            </Text>
                                                                        )}
                                                                        <Text size="xs" c="dimmed">
                                                                            {lineItem.quantity.toFixed(
                                                                                2,
                                                                            )}{' '}
                                                                            {lineItem.unit} @{' '}
                                                                            {formatPrice(
                                                                                lineItem.unitPrice,
                                                                            )}
                                                                            /{lineItem.unit}
                                                                        </Text>
                                                                    </Stack>
                                                                    <Text size="sm">
                                                                        {formatPrice(
                                                                            lineItem.totalPrice,
                                                                        )}
                                                                    </Text>
                                                                </Group>
                                                            }
                                                            styles={{
                                                                body: {flex: 1},
                                                                labelWrapper: {flex: 1},
                                                            }}
                                                        />
                                                        {canEdit ? (
                                                            <Group gap={4} wrap="nowrap">
                                                                <ActionIcon
                                                                    variant="subtle"
                                                                    aria-label={`Edit ${lineItem.name}`}
                                                                    disabled={lineItem.isSynced}
                                                                    title={
                                                                        lineItem.isSynced
                                                                            ? 'Synced charges cannot be edited'
                                                                            : undefined
                                                                    }
                                                                    onClick={() =>
                                                                        openEditModal(lineItem)
                                                                    }
                                                                >
                                                                    <IconEdit size={16} />
                                                                </ActionIcon>
                                                                <ActionIcon
                                                                    variant="subtle"
                                                                    color="red"
                                                                    aria-label={`Remove ${lineItem.name}`}
                                                                    onClick={() =>
                                                                        setPendingArchiveLineItem(
                                                                            lineItem,
                                                                        )
                                                                    }
                                                                >
                                                                    <IconTrash size={16} />
                                                                </ActionIcon>
                                                            </Group>
                                                        ) : null}
                                                    </Group>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </Stack>

                {unbilledLineItems.length > 0 && canEdit && (
                    <Group justify="space-between" align="center">
                        <Stack gap={0}>
                            <Text size="sm" fw={500}>
                                Selected total
                            </Text>
                            <Text size="lg" fw={700}>
                                {formatPrice(selectedTotal)}
                            </Text>
                        </Stack>
                        <Button
                            onClick={() => void handleCreateInvoice()}
                            disabled={
                                selectedLineItemIds.length === 0 ||
                                createInvoice.isPending ||
                                isProjectArchived
                            }
                            loading={createInvoice.isPending}
                        >
                            Create invoice
                        </Button>
                    </Group>
                )}

                {errorMessage && (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                )}
            </Stack>

            <ProjectFinancialLineItemModal
                opened={lineItemModalOpened}
                lineItem={editingLineItem}
                initialDraft={presetDraft}
                projectId={project.id}
                jobs={project.jobs}
                billingTerms={billingTerms}
                orderPoNumber={project.poNumber}
                onClose={closeLineItemModal}
            />

            <Modal
                opened={pendingArchiveLineItem != null}
                onClose={() => setPendingArchiveLineItem(undefined)}
                title="Remove charge"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Remove {pendingArchiveLineItem?.name} from this order? This charge will no
                        longer be available to invoice.
                    </Text>
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            onClick={() => setPendingArchiveLineItem(undefined)}
                            disabled={archiving}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            onClick={() => void handleArchiveLineItem()}
                            loading={archiving}
                        >
                            Remove charge
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Paper>
    )
}
